import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector, useAppDispatch } from '../../../../../../app/hooks';
import { getCompanyConfigData } from '../../../../../../store/company/service';
import { getDealTermsLogs, postCreateOrUpdateDealTermsLog } from '../Store/DealsHelper';
import { StatusDropdownOption } from '../../../../../../common/_custom/StatusDropdown/StatusDropdown';
import { DealTermsLogType, DealTermsLogs } from './DealTermsLoggingDrawer';
import { useDealsList } from '../useDealsList';
import { DEAL, DealStatus } from '../../../../../../store/investor/reducer';
import { slateToHtml } from '../../../../../../common/TextEditor/utils/SlateSerialisationUtil';
import { fallbackDealStatusOption } from '../DealsList';
import { setDealTermsLoggingDrawerPrefilledStatus } from '../../../../../../store/investor/action';
interface UseDealTermsLoggingDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    investorDealId: string;
}

export const useDealTermsLoggingDrawer = ({ isOpen, onClose, investorDealId }: UseDealTermsLoggingDrawerProps) => {
    const dispatch = useAppDispatch();
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const primaryEmail = useAppSelector((state) => state.user.userData.primaryEmail);
    const userData = useAppSelector((state) => state.user.userData);
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);
    const prefilledStatus = useAppSelector((state) => state.investor.dealTermsLoggingDrawer.prefilledStatus);

    const [dealTermsLoggingConfig, setDealTermsLoggingConfig] = useState<any>(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [dealTermsLogs, setDealTermsLogs] = useState<DealTermsLogs>({ dealTermsLogs: [] });
    const [runtimeLogs, setRuntimeLogs] = useState<DealTermsLogType[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<StatusDropdownOption>(
        dealStatusConfig?.statusDropdownOptionsMap?.[DealStatus.SHARED] || fallbackDealStatusOption?.[DealStatus.SHARED],
    );
    const [hasChanges, setHasChanges] = useState(false);
    const [expandedLogStatus, setExpandedLogStatus] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [deletingLogIds, setDeletingLogIds] = useState<Set<string>>(new Set());

    const latestLogRef = useRef<HTMLDivElement>(null);
    const originalLogsRef = useRef<DealTermsLogs>({ dealTermsLogs: [] });
    const shouldExpandNewLogRef = useRef<string | null>(null);

    const primaryCtaText = dealTermsLoggingConfig?.mainConfig?.additionalDetailsConfig?.pages?.[0]?.ctas?.primary?.text;
    const secondaryCtaText = dealTermsLoggingConfig?.mainConfig?.additionalDetailsConfig?.pages?.[0]?.ctas?.secondary?.text;
    const headerCtsText = dealTermsLoggingConfig?.mainConfig?.additionalDetailsConfig?.pages?.[0]?.ctas?.header?.text;
    const dropdownOptions = dealStatusConfig?.[DEAL]?.filter((option) => option.value !== DealStatus.INTERESTED) || [];

    // Calculate the highest status index reached from deal progression history
    const highestStatusIndexReached = useMemo(() => {
        const allLogs = [...(dealTermsLogs?.dealTermsLogs || []), ...runtimeLogs];
        if (allLogs.length === 0) return -1;

        let highestIndex = -1;
        allLogs.forEach((log) => {
            const statusIndex = dropdownOptions.findIndex((option) => option.value === log.logStatus);
            if (statusIndex > highestIndex) {
                highestIndex = statusIndex;
            }
        });

        return highestIndex;
    }, [dealTermsLogs, runtimeLogs, dropdownOptions]);

    const fetchConfig = async () => {
        try {
            setIsLoading(true);
            const res = await getCompanyConfigData({
                accountType: userData.accountType,
                investorOrgId: investorOrgId,
                primaryEmail: primaryEmail,
                configName: 'AICA_DEAL_LOGGING_DETAILS',
            });
            if (res.data) {
                setDealTermsLoggingConfig(res.data);
            }
        } catch (error) {
            toast.error('Error fetching deal terms logging config');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDealTermsLogsData = async () => {
        try {
            await getDealTermsLogs(investorDealId, {
                onSuccess: (data) => {
                    const newLogs = { dealTermsLogs: data };
                    setDealTermsLogs(newLogs);
                    originalLogsRef.current = newLogs;
                },
                onError: () => {
                    toast.error('Failed to fetch deal terms logs');
                },
            });
        } catch (error) {
            toast.error('Error fetching deal terms logs');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = (option: StatusDropdownOption) => {
        setSelectedStatus(option);
        setHasChanges(true);

        const allLogs = [...(dealTermsLogs?.dealTermsLogs || []), ...runtimeLogs];
        const lastLog = allLogs[allLogs.length - 1];

        const newLog: DealTermsLogType = {
            changeDate: Date.now(),
            tempId: `temp-${Date.now()}`,
            investorDealId: lastLog.investorDealId,
            dealId: lastLog.dealId,
            logStatus: option.value,
            logDate: new Date().toISOString().substring(0, 10),
            dealTerms: { ...lastLog.dealTerms, logDate: new Date().toISOString().substring(0, 10), documents: [] },
            owner: {
                name: userData.userName,
                userId: userData.userId,
                activityBy: userData.userName,
                activityDate: new Date().toISOString(),
                ownerType: userData.accountType,
            },
        };

        setRuntimeLogs((prev) => [...prev, newLog]);
        setExpandedLogStatus(newLog.tempId || null);

        setTimeout(() => {
            latestLogRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 320);
    };

    const sourceBasedActions = (source: string) => {
        if (source === 'View Deal') {
            const allLogs = [...(dealTermsLogs?.dealTermsLogs || []), ...runtimeLogs];
            const latestLog = allLogs[allLogs.length - 1];
            if (latestLog) {
                setExpandedLogStatus(latestLog.id || latestLog.tempId || null);
                setTimeout(() => {
                    latestLogRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }, 600);
            }
        } else {
            setExpandedLogStatus(null);
        }
    };

    const handleLogUpdate = (updatedLog: DealTermsLogType) => {
        if (updatedLog.id) {
            setDealTermsLogs({
                dealTermsLogs: dealTermsLogs.dealTermsLogs.map((log) => (log.id === updatedLog.id ? updatedLog : log)),
            });
        } else {
            setRuntimeLogs((prev) => prev.map((log) => (log.tempId === updatedLog.tempId ? updatedLog : log)));
        }
    };

    const handleDeleteRuntimeLog = (tempId: string) => {
        setRuntimeLogs((prev) => prev.filter((log) => log.tempId !== tempId));
    };

    const handleDropdownToggle = (log: DealTermsLogType, isExpanded: boolean) => {
        const logIdentifier = log.id || log.tempId || null;
        setExpandedLogStatus(isExpanded ? logIdentifier : null);
    };

    const handleClose = () => {
        setExpandedLogStatus(null);
        setRuntimeLogs([]);
        setDealTermsLogs(originalLogsRef.current);
        setHasChanges(false);
        // Clear prefilled status from Redux when drawer closes
        dispatch(setDealTermsLoggingDrawerPrefilledStatus(null));
        onClose();
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const combinedLogs: any = processPayloadForSave();

            combinedLogs.requestPayload.dealTermsLogs.forEach((log) => {
                log.logDate = new Date(log.logDate).toISOString().split('T')[0];
            });

            await postCreateOrUpdateDealTermsLog(combinedLogs, {
                onSuccess: () => {
                    setHasChanges(false);
                    setExpandedLogStatus(null);
                    onClose();
                },
                onError: () => {
                    toast.error('Failed to save deal terms logs');
                },
            });
        } catch (error) {
            toast.error('Error saving deal terms logs');
            console.error('Error saving deal terms logs:', error);
        } finally {
            setIsLoading(false);
            setRuntimeLogs([]);
        }
    };

    const processPayloadForSave = () => {
        return {
            requestPayload: {
                dealTermsLogs: [
                    ...(dealTermsLogs?.dealTermsLogs || [])?.map((dealTermsLog) => {
                        return {
                            ...dealTermsLog,
                            dealTerms: {
                                ...(dealTermsLog?.dealTerms || {}),
                                ...(dealTermsLog?.dealTerms?.dealNotes && {
                                    dealNotes: [
                                        {
                                            note: slateToHtml(dealTermsLog?.dealTerms?.dealNotes),
                                            noteBy: userData.userName,
                                            noteDate: new Date().toISOString(),
                                        },
                                    ],
                                }),
                            },
                        };
                    }),
                    ...runtimeLogs,
                ],
                dealProposerId: userData?.investorOrgId,
            },
        };
    };

    const handleCancel = () => {
        setDealTermsLogs(originalLogsRef.current);
        setRuntimeLogs([]);
        setExpandedLogStatus(null);
        setIsFormValid(false);
        // Clear prefilled status from Redux when drawer closes
        dispatch(setDealTermsLoggingDrawerPrefilledStatus(null));
        onClose();
    };

    const handleDeleteWithAnimation = (tempId: string) => {
        // First collapse the dropdown
        setExpandedLogStatus(null);

        // Wait for dropdown collapse animation to complete
        setTimeout(() => {
            // Then start the deletion animation
            setDeletingLogIds((prev) => new Set([...prev, tempId]));

            // Finally remove the item after the deletion animation
            setTimeout(() => {
                handleDeleteRuntimeLog(tempId);
                setDeletingLogIds((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(tempId);
                    return newSet;
                });
            }, 500); // Deletion animation duration
        }, 700); // Dropdown collapse duration
    };

    useEffect(() => {
        if (originalLogsRef.current && dealTermsLogs) {
            const stringifyValue = (obj: any): any => {
                if (obj === null || obj === undefined) return String(obj);
                if (typeof obj !== 'object') return String(obj);
                if (Array.isArray(obj)) {
                    return obj.map(stringifyValue);
                }
                const result: any = {};
                for (const key in obj) {
                    result[key] = stringifyValue(obj[key]);
                }
                return result;
            };

            const originalStringified = stringifyValue(originalLogsRef.current);
            const currentStringified = stringifyValue(dealTermsLogs);

            const hasLogChanges = JSON.stringify(originalStringified) !== JSON.stringify(currentStringified);
            const hasRuntimeLogChanges = runtimeLogs.length > 0;
            setHasChanges(hasLogChanges || hasRuntimeLogChanges);
        }
    }, [dealTermsLogs, runtimeLogs]);

    useEffect(() => {
        setIsInitialLoading(true);
        Promise.all([fetchConfig(), isOpen && investorDealId ? fetchDealTermsLogsData() : Promise.resolve()]).finally(() => {
            setIsInitialLoading(false);
        });
    }, [isOpen, investorDealId]);

    const processPrefilledStatus = () => {
        // Find the status option from the prefilled status value
        const statusOption =
            dealStatusConfig?.statusDropdownOptionsMap?.[prefilledStatus!] ||
            dropdownOptions.find((option) => option.value === prefilledStatus);

        if (statusOption) {
            // Set the selected status
            setSelectedStatus(statusOption as StatusDropdownOption);

            // Clear the Redux state after using it
            dispatch(setDealTermsLoggingDrawerPrefilledStatus(null));

            // Automatically trigger handleStatusChange to create a new log entry
            // This will create a new runtime log with the prefilled status
            // Only if we have at least one log to base the new log on
            const allLogs = [...(dealTermsLogs?.dealTermsLogs || []), ...runtimeLogs];
            if (allLogs.length > 0) {
                const lastLog = allLogs[allLogs.length - 1];
                const newLog: DealTermsLogType = {
                    changeDate: Date.now(),
                    tempId: `temp-${Date.now()}`,
                    investorDealId: lastLog.investorDealId,
                    dealId: lastLog.dealId,
                    logStatus: statusOption.value,
                    logDate: new Date().toISOString().substring(0, 10),
                    dealTerms: { ...lastLog.dealTerms, logDate: new Date().toISOString().substring(0, 10), documents: [] },
                    owner: {
                        name: userData.userName,
                        userId: userData.userId,
                        activityBy: userData.userName,
                        activityDate: new Date().toISOString(),
                        ownerType: userData.accountType,
                    },
                };

                setRuntimeLogs((prev) => [...prev, newLog]);
                // Store the tempId to expand after the log is rendered
                shouldExpandNewLogRef.current = newLog.tempId || null;
                setHasChanges(true);
            }
        }
    };

    // Handle prefilled status from Redux when drawer opens
    useEffect(() => {
        // Wait for initial loading to complete and ensure we have config and logs loaded
        if (isOpen && !isInitialLoading && prefilledStatus && dealStatusConfig) {
            processPrefilledStatus();
        }
    }, [isOpen, isInitialLoading, prefilledStatus, dealStatusConfig, dealTermsLogs, runtimeLogs, dropdownOptions, userData, dispatch]);

    // Expand the newly created log when it's added to runtimeLogs (for prefilled status from DealsRow)
    useEffect(() => {
        if (shouldExpandNewLogRef.current) {
            const logToExpand = runtimeLogs.find((log) => log.tempId === shouldExpandNewLogRef.current);
            if (logToExpand) {
                // Set expanded status - use requestAnimationFrame to ensure DOM is updated
                requestAnimationFrame(() => {
                    setExpandedLogStatus(shouldExpandNewLogRef.current);
                    shouldExpandNewLogRef.current = null;

                    // Scroll to the expanded log after a short delay
                    setTimeout(() => {
                        latestLogRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }, 150);
                });
            }
        }
    }, [runtimeLogs]);

    // Function to handle status display for investor deals
    const getStatusDropdownOption = (status: string): StatusDropdownOption => {
        // const statusMap = {
        //     [DealStatus.SHARED]: DealStatus.FIRST_DEAL_SHARED,
        //     [DealStatus.INTERESTED]: DealStatus.INTEREST_RECEIVED,
        // };
        return (dealStatusConfig?.statusDropdownOptionsMap?.[status] ||
            dealStatusConfig?.statusDropdownOptionsMap?.[DealStatus.FIRST_DEAL_SHARED]) as StatusDropdownOption;
    };

    return {
        // States
        dealTermsLoggingConfig,
        isFormValid,
        dealTermsLogs,
        runtimeLogs,
        selectedStatus,
        hasChanges,
        expandedLogStatus,
        isLoading,
        isInitialLoading,
        latestLogRef,
        deletingLogIds,

        // Constants
        primaryCtaText,
        secondaryCtaText,
        headerCtsText,
        dropdownOptions,
        highestStatusIndexReached,

        // Methods
        setIsFormValid,
        handleStatusChange,
        handleLogUpdate,
        handleDeleteRuntimeLog,
        handleDropdownToggle,
        handleClose,
        handleSave,
        sourceBasedActions,
        handleCancel,
        setExpandedLogStatus,
        handleDeleteWithAnimation,
        getStatusDropdownOption,
    };
};
